import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, RefreshCw, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 15000;    // Check every 15s
const FAIL_THRESHOLD = 2;       // Show banner after 2 consecutive failures
const RECOVERY_DISPLAY_MS = 4000; // Show "Back online" for 4s

/**
 * Global connection status monitor.
 * Polls the API health endpoint and shows a non-blocking banner
 * when the backend is unreachable (during deploys/restarts).
 */
export default function ConnectionStatus() {
  const [status, setStatus] = useState('online'); // 'online' | 'offline' | 'recovering'
  const failCount = useRef(0);
  const wasOffline = useRef(false);
  const recoveryTimer = useRef(null);

  const checkHealth = useCallback(async () => {
    try {
      const token = localStorage.getItem('orbis_token');
      if (!token) return; // Not logged in — skip

      const resp = await fetch(`${API_URL}/api/auth/me`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(5000),
      });

      // 401 = server is alive (just not authed), 200 = all good
      if (resp.ok || resp.status === 401) {
        failCount.current = 0;

        if (wasOffline.current) {
          // Just recovered from offline
          wasOffline.current = false;
          setStatus('recovering');
          recoveryTimer.current = setTimeout(() => setStatus('online'), RECOVERY_DISPLAY_MS);
        } else {
          setStatus('online');
        }
      } else {
        failCount.current++;
        if (failCount.current >= FAIL_THRESHOLD) {
          wasOffline.current = true;
          setStatus('offline');
        }
      }
    } catch {
      failCount.current++;
      if (failCount.current >= FAIL_THRESHOLD) {
        wasOffline.current = true;
        setStatus('offline');
      }
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(checkHealth, POLL_INTERVAL);
    return () => {
      clearInterval(interval);
      if (recoveryTimer.current) clearTimeout(recoveryTimer.current);
    };
  }, [checkHealth]);

  if (status === 'online') return null;

  return (
    <AnimatePresence>
      {status !== 'online' && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            padding: '10px 20px',
            fontFamily: 'var(--orbis-font)',
            fontSize: 13,
            fontWeight: 500,
            background: status === 'offline'
              ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.95), rgba(217, 119, 6, 0.95))'
              : 'linear-gradient(135deg, rgba(52, 211, 153, 0.95), rgba(16, 185, 129, 0.95))',
            color: status === 'offline' ? '#1a1a1a' : '#064e3b',
            backdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${status === 'offline' ? 'rgba(245,158,11,0.3)' : 'rgba(52,211,153,0.3)'}`,
          }}
        >
          {status === 'offline' ? (
            <>
              <WifiOff className="w-4 h-4" />
              <span>Orbis is updating — your work is saved. Reconnecting</span>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              <span>Back online</span>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
